import {
  getAnalytics,
  logEvent,
  setUserProperties,
  setUserId,
} from "firebase/analytics";
import { app } from "./firebase";

// Initialize Analytics
export const analytics = getAnalytics(app);

/**
 * Log user login/session start
 * Called on auth success or app initialization
 */
export const trackUserSession = (userId, email) => {
  try {
    // Set user ID for user-centric analytics
    setUserId(analytics, userId);

    // Set user properties for segmentation
    setUserProperties(analytics, {
      user_segment: "registered_user",
      email: email || "unknown",
      signup_date: new Date().toISOString().split("T")[0],
    });

    // Log login/session start
    logEvent(analytics, "user_session_start", {
      timestamp: new Date().toISOString(),
      platform: getPlatform(),
    });
  } catch (error) {
    console.error("Analytics: Failed to track user session", error);
  }
};

/**
 * Log meal logging feature usage
 * Call when user logs a meal
 * @param uid - User ID
 * @param description - Meal description
 * @param calories - Calorie estimate
 */
export const trackMealLogged = (uid, description, calories) => {
  try {
    logEvent(analytics, "meal_logged", {
      uid: uid || "unknown",
      meal_description: String(description).substring(0, 100) || "unknown",
      calories: Math.round(Number(calories) || 0),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track meal logged", error);
  }
};

/**
 * Log workout tracking feature usage
 * Call when user logs a workout
 */
export const trackWorkoutLogged = (uid, date) => {
  try {
    logEvent(analytics, "workout_logged", {
      uid: uid || "unknown",
      dateLogged: date || new Date().toISOString().split("T")[0],
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track workout logged", error);
  }
};

/**
 * Log weight journey tracking
 * Call when user logs a new weight
 */
export const trackWeightJourneyLogged = (uid, newWeight, date) => {
  try {
    logEvent(analytics, "weight_journey_logged", {
      uid: uid || "unknown",
      newWeight: Math.round(Number(newWeight) || 0),
      dateLogged: date || new Date().toISOString().split("T")[0],
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track weight journey logged", error);
  }
};

/**
 * Log daily quote view
 * Tracks engagement with motivational quotes
 */
export const trackDailyQuoteViewed = (quoteDay) => {
  try {
    logEvent(analytics, "daily_quote_viewed", {
      quote_day_of_year: quoteDay || new Date().getDay(),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track daily quote viewed", error);
  }
};

/**
 * Log app screen/page view
 * Tracks which screens users spend time on
 */
export const trackScreenView = (screenName) => {
  try {
    logEvent(analytics, "screen_view", {
      screen_name: screenName || "unknown",
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track screen view", error);
  }
};

/**
 * Log user engagement metrics
 * Call periodically to track user activity
 */
export const trackUserEngagement = (mealCount, workoutCount) => {
  try {
    logEvent(analytics, "user_engagement", {
      meals_logged_today: Math.round(Number(mealCount) || 0),
      workouts_logged_today: Math.round(Number(workoutCount) || 0),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track user engagement", error);
  }
};

/**
 * Log feature error/issue
 * Track errors for debugging and feature reliability
 */
export const trackFeatureError = (featureName, errorMessage) => {
  try {
    logEvent(analytics, "feature_error", {
      feature: featureName || "unknown",
      error: String(errorMessage).substring(0, 100) || "unknown error",
      timestamp: new Date().toISOString(),
    });

    console.error(
      "[Analytics] Feature error tracked:",
      featureName,
      errorMessage,
    );
  } catch (error) {
    console.error("Failed to track feature error", error);
  }
};

/**
 * Helper: Detect platform
 */
function getPlatform() {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("iphone") || userAgent.includes("ipad")) return "ios";
  if (userAgent.includes("android")) return "android";
  return "web";
}
